import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [message, setMessage] = useState("Confirming your payment…");
  const attemptsRef = useRef(0);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setMessage("No session id in URL.");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          setMessage("Payment confirmed. Welcome to Pro! 🎉");
          try {
            await refreshUser();
          } catch (e) {
            // Non-critical: banner refresh happens on next page load anyway.
            console.debug("refreshUser after payment failed:", e?.message);
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("error");
          setMessage("This checkout session expired. Please try again.");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          setMessage("Still processing — check your email for confirmation.");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("error");
          setMessage(e?.response?.data?.detail || "Could not verify payment.");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="space-y-4" data-testid="upgrade-success-page">
      <h1 className="font-display text-3xl font-black">Payment</h1>
      <div
        className="nb-card-lg p-8 text-center space-y-4"
        style={{
          background:
            status === "success" ? "#b5e4ca" : status === "error" ? "#f4c2c2" : "#fdecc8",
        }}
      >
        {status === "polling" && (
          <>
            <Loader2 size={48} strokeWidth={2.5} className="mx-auto animate-spin" />
            <div className="font-display font-black text-2xl" data-testid="payment-status-text">
              {message}
            </div>
          </>
        )}
        {status === "success" && (
          <>
            <Crown size={48} strokeWidth={2.5} className="mx-auto" />
            <div className="font-display font-black text-2xl" data-testid="payment-status-text">
              {message}
            </div>
            <div className="text-sm font-bold">
              Forecast & Multi-Account are now unlocked.
            </div>
            <button
              data-testid="go-home"
              onClick={() => navigate("/")}
              className="nb-btn nb-btn-dark inline-flex"
            >
              Go to dashboard
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={48} strokeWidth={2.5} className="mx-auto" />
            <div className="font-display font-black text-2xl" data-testid="payment-status-text">
              {message}
            </div>
            <Link to="/upgrade" className="nb-btn nb-btn-secondary inline-flex">
              Back to upgrade
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <CheckCircle2 size={48} strokeWidth={2.5} className="mx-auto" />
            <div className="font-display font-black text-2xl" data-testid="payment-status-text">
              {message}
            </div>
            <Link to="/" className="nb-btn nb-btn-dark inline-flex">
              Continue
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
